import { Header, Button, Box, Text, Nav, Menu, ResponsiveContext, Layer, List, Heading } from 'grommet';
import { Close, Dashboard, Logout, SettingsOption, User } from 'grommet-icons';
import { TwitchAuth } from '../auth/TwitchAuth';
import { WhatYouWishLogo } from '../../icons/WhatYouWishLogo';
import { AUTH_STATUSES, useAuth } from '../../auth';
import { LanguageSelector } from './LanguageSelector';
import { useTranslation } from 'react-i18next';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogout } from '../../auth/useLogout';

export const AppHeader = () => {

  const responsive = useContext(ResponsiveContext);
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { status, user: currentUser, isAuthenticated } = useAuth();
  const isAnonymous = status === AUTH_STATUSES.ANONYMOUS;
  const isAuthError = status === AUTH_STATUSES.ERROR;
  const isAuthInProgress = [
    AUTH_STATUSES.CHECKING,
    AUTH_STATUSES.REDIRECTING,
    AUTH_STATUSES.LOGGING_OUT
  ].includes(status);
  const { logout, isLogoutRetryable } = useLogout();

  const myAccountItems = [
    {
      label: <Text weight={'bold'}>{t('dashboard')}</Text>,
      icon: <Dashboard />,
      onClick: () => navigate('/dashboard/events')
    },
    {
      label: <Text weight={'bold'}>{t('settings')}</Text>,
      icon: (<SettingsOption />),
      onClick: () => navigate('/settings/overlay-settings')
    },
    {
      label: <Text weight={'bold'}>{t('signOut')}</Text>,
      icon: (<Logout />),
      onClick: () => handleSignOut()
    }
  ]

  const handleSignOut = () => {
    logout();
  }

  return (
    <Header
      fill="horizontal"
      pad={{ horizontal: 'medium', vertical: 'small' }}
      background="background-front">
      <Button>
        <Box
          direction="row"
          align="start"
          gap="medium"
          pad={{ vertical: 'none' }}
          responsive={false}
        >
          <Box direction="row" gap="xsmall" wrap onClick={() => navigate('/')} focusIndicator={false}>
            <WhatYouWishLogo />
          </Box>
        </Box>
      </Button>
      <Box direction='row'>
        {!['xsmall', 'small', 'medium'].includes(responsive) &&
          <FullHeaderNavMenu
            isAuthenticated={isAuthenticated}
            isAnonymous={isAnonymous}
            isAuthError={isAuthError}
            isAuthInProgress={isAuthInProgress}
            myAccountItems={myAccountItems}
            currentUser={currentUser}
            isLogoutRetryable={isLogoutRetryable}
            handleSignOut={handleSignOut}
          />}
        {['xsmall', 'small', 'medium'].includes(responsive) &&
          <MobileHeaderNavMenu
            isAuthenticated={isAuthenticated}
            isAnonymous={isAnonymous}
            isAuthError={isAuthError}
            isAuthInProgress={isAuthInProgress}
            currentUser={currentUser}
            handleSignOut={handleSignOut}
            isLogoutRetryable={isLogoutRetryable}
          />}
      </Box>
    </Header>
  );
};

const FullHeaderNavMenu = ({
  isAuthenticated,
  isAnonymous,
  isAuthError,
  isAuthInProgress,
  myAccountItems,
  currentUser,
  isLogoutRetryable,
  handleSignOut
}) => {

  const { t } = useTranslation('common');
  const navigate = useNavigate();

  return (
    <Nav direction="row" background="background-front" pad="none" align='end'>
      <Button label={t('auctionHeaderButton')} onClick={() => navigate('/auction')} />
      <Button label={t('memesHeaderButton')} onClick={() => navigate('/memes')} badge={<Box background={'brand'} round="xsmall" pad={{ top: '1px', bottom: '1px', left: '2px', right: '2px' }}><Text size='xsmall'>beta</Text></Box>} />
      {/* <Button label={'Snowerlay'} href='https://snowerlay.netlify.app' target='_blank' style={{ fontFamily: '\"Press Start 2P\", system-ui', fontSize: '14px', fontWeight: 400, fontStyle: 'normal', color: '#0a7400', textShadow: '2px 2px #ff0000' }}/> */}
      {(isAnonymous || isAuthError) && !isLogoutRetryable && (
        <TwitchAuth />
      )}
      {isAuthInProgress && (
        <Button busy label="User" />
      )}
      {isAuthenticated && (
        <Box direction='row' gap='small'>
          <Menu label={<Box gap='small' direction='row' align='center'>
            <User />
            <Text weight={'bold'}>{currentUser.username}</Text>
          </Box>} items={myAccountItems} size='small' />
        </Box>
      )}
      {isLogoutRetryable && (
        <Box direction="row" align="center" gap="small" role="alert">
          <Text size="small">{t('logoutError')}</Text>
          <Button label={t('logoutRetry')} onClick={handleSignOut} data-testid="logout-retry" />
        </Box>
      )}
      <LanguageSelector />
    </Nav>
  )
}

const MobileHeaderNavMenu = ({
  isAuthenticated,
  isAnonymous,
  isAuthError,
  isAuthInProgress,
  currentUser,
  handleSignOut,
  isLogoutRetryable
}) => {

  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const [showMenu, setShowMenu] = useState();

  const headerItems = [
    {
      label: t('auctionHeaderButton'),
      onClick: () => navigate('/auction')
    },
    {
      label: t('memesHeaderButton'),
      onClick: () => navigate('/memes')
    }
  ]

  const myAccountItems = [
    {
      label: t('dashboard'),
      icon: <Dashboard />,
      onClick: () => navigate('/dashboard/events')
    },
    {
      label: t('settings'),
      icon: (<SettingsOption />),
      onClick: () => navigate('/settings/overlay-settings')
    },
    {
      label: t('signOut'),
      icon: (<Logout />),
      onClick: () => handleSignOut()
    }
  ]

  return (
    <Nav direction="row" background="background-front" pad="none" align='end'>
      <Button label={t('menuBtnLabel')} onClick={() => setShowMenu(true)} />
      <LanguageSelector />
      {showMenu && <Layer position="right" full="vertical" onEsc={() => setShowMenu(false)} onClickOutside={() => setShowMenu(false)}>
        <Box>
          <Box direction='row' justify='between' align='center' pad={{ left: 'medium', right: 'medium' }}>
            <Heading level={2}>{t('mainMenuHeader')}</Heading>
            <Button icon={<Close />} onClick={() => setShowMenu(false)} />
          </Box>
          <List
            primaryKey={(item) => <Box direction='row' gap='small' align='center'><Text>{item.label}</Text></Box>}
            data={headerItems}
            onClickItem={({ item }) => item.onClick()}
          />
          {(isAnonymous || isAuthError) && !isLogoutRetryable && (
            <Box pad={{ left: 'medium', right: 'medium', top: 'small', bottom: 'small' }}><TwitchAuth /></Box>
          )}
          {isAuthInProgress && (
            <Button busy label="User" />
          )}
          {isAuthenticated && <Box>
            <Box direction='row' gap='small' align='center' pad={{ left: 'medium', right: 'medium', top: 'small', bottom: 'small' }}>
              <User />
              <Text weight={'bold'}>{currentUser.username}</Text>
            </Box>
            <List
              primaryKey={(item) => <Box direction='row' gap='small' align='center'>{item.icon}<Text>{item.label}</Text></Box>}
              data={myAccountItems}
              onClickItem={({ item }) => item.onClick()}
            />
          </Box>}
          {isLogoutRetryable && <Box gap="small" pad="medium" role="alert">
            <Text>{t('logoutError')}</Text>
            <Button label={t('logoutRetry')} onClick={handleSignOut} data-testid="logout-retry" />
          </Box>}
        </Box>
      </Layer>}
    </Nav>
  )
}
